import React from 'react';
import { ArrowUpRight, Download, Github, Linkedin, MapPin } from 'lucide-react';
import { focusAreas, profile } from '../data/profile';

const delay = (ms: number) => ({ animationDelay: `${ms}ms` });

const LETTER_MS = 45;

/** Splits a word into letters that rise in one after another. */
const Letters: React.FC<{ text: string; start: number }> = ({ text, start }) => (
  <>
    {text.split('').map((char, i) => (
      <span key={i} className="letter inline-block" style={delay(start + i * LETTER_MS)}>
        {char}
      </span>
    ))}
  </>
);

/**
 * Head-to-waist portrait. An amber outline sits offset behind the card like a second frame;
 * on hover the photo lifts off it and warms to full colour.
 */
const Portrait: React.FC = () => (
  <figure
    className="intro group relative mx-auto w-full max-w-[22rem] min-w-0 sm:max-w-[26rem] lg:ml-auto lg:mr-3"
    style={delay(180)}
  >
    <div
      aria-hidden
      className="absolute inset-0 translate-x-3 translate-y-3 rounded-[1.75rem] border border-accent/30 transition-transform duration-500 ease-out group-hover:translate-x-4 group-hover:translate-y-4"
    />
    <div className="relative overflow-hidden rounded-[1.75rem] bg-ink-900 shadow-[0_30px_80px_-30px_rgb(255_181_71/0.35)] ring-1 ring-ink-600 transition-transform duration-500 ease-out group-hover:-translate-x-1 group-hover:-translate-y-1">
      <img
        src={profile.photo}
        alt={`Portrait of ${profile.name}`}
        width={1000}
        height={1500}
        className="aspect-[4/5] w-full object-cover object-[50%_40%] saturate-[0.85] transition duration-700 ease-out group-hover:scale-[1.03] group-hover:saturate-100"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-transparent via-45% to-ink/20"
      />
      <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-white/10 bg-ink/55 px-2.5 py-1 font-mono text-[11px] text-fg-muted backdrop-blur-md">
        <MapPin size={11} className="text-accent" />
        {profile.location}
      </span>
      <figcaption className="absolute inset-x-0 bottom-0 px-5 pb-5">
        <span className="block font-serif text-3xl leading-none text-fg">{profile.name}</span>
        <span className="mt-2 block font-mono text-[11px] text-fg-muted">
          Backend <span className="text-accent/70">·</span> Distributed systems <span className="text-accent/70">·</span>{' '}
          Applied AI
        </span>
      </figcaption>
    </div>
  </figure>
);

const Hero: React.FC = () => (
  <section id="top" className="pt-28 sm:pt-32 lg:pt-36">
    <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:gap-16">
      <div className="min-w-0">
        <a
          href="#experience"
          className="intro inline-flex items-center gap-2.5 rounded-full border border-accent/30 bg-accent/10 py-1 pl-2.5 pr-3.5 text-xs text-accent transition-colors hover:bg-accent/15"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
          </span>
          Incoming at Caterpillar · SDE Intern
        </a>

        <h1
          className="mt-6 font-serif text-[clamp(3.5rem,9vw,6.25rem)] leading-[0.92] tracking-tight text-fg"
          aria-label={profile.name}
        >
          <Letters text="Abhinav" start={80} />
          <br />
          <Letters text="Garg" start={80 + 7 * LETTER_MS} />
          <span className="dot-pop inline-block text-accent" style={delay(80 + 11 * LETTER_MS + 200)}>
            .
          </span>
        </h1>

        <p className="intro mt-6 max-w-xl text-lg leading-relaxed text-fg-muted sm:text-xl" style={delay(160)}>
          Backend engineer building <span className="text-fg">distributed systems</span>,{' '}
          <span className="text-fg">sandboxed execution engines</span> and{' '}
          <span className="text-fg">applied-AI tooling</span> that hold up in production.
        </p>

        <dl className="intro mt-7 max-w-xl divide-y divide-ink-700 border-y border-ink-700 text-sm" style={delay(220)}>
          <div className="flex gap-4 py-3">
            <dt className="w-14 shrink-0 pt-px font-mono text-[11px] uppercase tracking-wider text-fg-faint">Now</dt>
            <dd className="text-fg">
              Project Intern, QA Tooling &amp; Applied AI <span className="text-fg-muted">· Samsung R&amp;D Institute India</span>
            </dd>
          </div>
          <div className="flex gap-4 py-3">
            <dt className="w-14 shrink-0 pt-px font-mono text-[11px] uppercase tracking-wider text-fg-faint">Study</dt>
            <dd className="text-fg">
              B.Tech CSE (AI &amp; ML), VIT <span className="text-fg-muted">· 9.34 CGPA · ’27</span>
            </dd>
          </div>
          <div className="flex gap-4 py-3">
            <dt className="w-14 shrink-0 pt-px font-mono text-[11px] uppercase tracking-wider text-fg-faint">Led</dt>
            <dd className="text-fg">
              Technical Manager <span className="text-fg-muted">· Riviera &amp; Gravitas, VIT</span>
            </dd>
          </div>
        </dl>

        <div className="intro mt-8 flex flex-wrap items-center gap-3" style={delay(280)}>
          <a
            href={profile.resume}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-ink transition duration-200 hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-[0_10px_30px_-10px_rgba(255,181,71,0.7)]"
          >
            <Download size={16} />
            Download résumé
          </a>
          <a
            href={`mailto:${profile.email}`}
            className="group inline-flex items-center gap-1.5 rounded-md border border-ink-600 px-5 py-2.5 text-sm text-fg transition duration-200 hover:-translate-y-0.5 hover:border-fg-faint"
          >
            Get in touch
            <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
          <div className="flex items-center">
            {[
              { href: profile.github, label: 'GitHub', icon: <Github size={18} /> },
              { href: profile.linkedin, label: 'LinkedIn', icon: <Linkedin size={18} /> },
            ].map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="grid h-10 w-10 place-items-center rounded-md text-fg-muted transition-colors hover:bg-ink-800 hover:text-fg"
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </div>

      <Portrait />
    </div>

    <div className="reveal mt-20 sm:mt-24">
      <p className="mb-4 font-mono text-xs uppercase tracking-[0.2em] text-fg-faint">What I work on</p>
      <ul className="grid gap-px overflow-hidden rounded-2xl border border-ink-700 bg-ink-700 md:grid-cols-3">
        {focusAreas.map((area, i) => (
          <li key={area.title} className="spotlight group flex flex-col bg-ink-900 p-6 sm:p-7">
            <span className="font-mono text-xs text-accent">0{i + 1}</span>
            <h3 className="mt-4 font-serif text-3xl leading-tight text-fg">{area.title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-fg-muted">{area.body}</p>
            <p className="mt-auto flex flex-wrap gap-x-3 gap-y-1 pt-6 font-mono text-[11px] text-fg-faint">
              {area.proof.map((item) => (
                <span key={item} className="transition-colors group-hover:text-fg-muted">
                  <span className="text-accent/70">→</span> {item}
                </span>
              ))}
            </p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export default Hero;
